import { useEffect } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { fetchDocument, fetchEntryBySlug, fetchMedia } from '../api/contentApi';
import { contentPath, typeInfo } from '../content/contentTypes';
import { MediaContext } from '../content/renderer/MediaContext';
import { getTemplate } from '../content/templates';
import { applySeo } from '../content/seo';
import { articleInfo } from '../content/articleInfo';
import StatusMessage from '../components/StatusMessage';
import LoadError from '../components/LoadError';
import { SITE_NAME } from '../siteConfig';
import useAsync from '../useAsync';

async function load(slug) {
  const [{ entry, movedTo }, media] = await Promise.all([fetchEntryBySlug(slug), fetchMedia()]);
  if (!entry) {
    // Old slug -> new slug redirect (spec section 54).
    return { redirect: movedTo ? contentPath(movedTo) : null };
  }
  const doc = await fetchDocument({ ...entry, slug });
  // Everything else the page needs (type, slug, dates, reading time) comes from the document itself.
  return { entry: articleInfo(doc), media, doc };
}

export default function ContentPage() {
  const { typeRoute, slug } = useParams();
  const location = useLocation();
  const { loading, error, data, reload } = useAsync(() => load(slug), [slug]);

  const doc = data?.doc;
  useEffect(() => {
    if (!doc) return;
    applySeo({ doc, entry: data.entry, media: { ...data.media, ...doc.media }, siteName: SITE_NAME, path: contentPath(data.entry) });
    if (location.hash) {
      document.getElementById(decodeURIComponent(location.hash.slice(1)))?.scrollIntoView();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, location.hash]);

  if (loading) {
    return <div className="blog-container"><StatusMessage title="Loading..." /></div>;
  }
  if (error) {
    return (
      <div className="blog-container">
        <LoadError error={error} onRetry={reload} what="this article" />
      </div>
    );
  }
  if (data.redirect) return <Navigate to={data.redirect} replace />;
  if (!data.entry) {
    return (
      <div className="blog-container">
        <StatusMessage title="Page not found">This content does not exist or has not been imported.</StatusMessage>
      </div>
    );
  }
  if (typeInfo(data.entry.type).route !== typeRoute) {
    return <Navigate to={contentPath(data.entry)} replace />;
  }

  const Template = getTemplate(doc.type);
  return (
    <MediaContext.Provider value={{ ...data.media, ...doc.media }}>
      <div className="blog-container">
        <Template entry={data.entry} doc={doc} />
      </div>
    </MediaContext.Provider>
  );
}
